/**
 * formatDuration — Apr 30 2026, rewritten May 4 2026
 *
 * Renders a game duration in human-readable hours+minutes format
 * instead of raw minutes or fractional hours.
 *
 * History: original version returned "1.5 hr" for 90 min and "1.35 hr"
 * for 80 min. Coach feedback (May 2026): "1.35 hr" is cognitive
 * overhead — nobody computes 0.35 × 60 = 21 in their head. The number
 * is also misleading for 80 min: 80/60 = 1.333 (≠ 1.35; the rounding
 * to nearest 0.05 produces 1.35). Same display, two different
 * underlying meanings.
 *
 * New rules — always hours+minutes, no fractional hours:
 *   • 60   → "1h"
 *   • 75   → "1h 15m"
 *   • 80   → "1h 20m"     ← the screenshot case
 *   • 90   → "1h 30m"
 *   • 120  → "2h"
 *   • 150  → "2h 30m"
 *   • 45   → "45m"        (still minutes-only when under an hour)
 *   • 30   → "30m"
 *   • 0 / null / undefined / negative / non-numeric → null
 *
 * "h" + "m" was picked over "1 hour 20 minutes" / "1 hr 20 min" for
 * compactness (these strings sit in tight pill rows next to start time
 * + arena name + RSVP counts). The unit letters are universally
 * understood in scheduling contexts and remain unambiguous.
 *
 * If a future caller wants the long-form ("1 hour 20 minutes") it
 * should be a separate utility (e.g. formatDurationLong) — keep this
 * one focused on compact display.
 *
 * Returns a string for any positive integer ≥ 1 minute, or null for
 * empty/zero/invalid input. Callers do:
 *   const dur = formatDuration(game.duration_minutes);
 *   if (dur) <span> · {dur}</span>
 */
export function formatDuration(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return null;
  // Round to nearest whole minute first — defensive against any caller
  // passing fractional minutes (shouldn't happen, but the database
  // column is INTEGER so the only fractions would come from arithmetic
  // upstream).
  const total = Math.round(n);
  if (total < 60) return `${total}m`;
  const hours = Math.floor(total / 60);
  const mins  = total % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}
